import { Compass } from "@phosphor-icons/react";
import { CountUp } from "@/components/marketing/Motion";
import {
  VariogramViz, ClassificationBars,
} from "@/components/marketing/SresVisuals";
import { IsometricBlockModel } from "@/components/marketing/SresPremiumVisuals";

/* ================================================================== *
 * <ResourceCockpit>                                                   *
 *                                                                     *
 * The signature SRES hero panel — used on the Landing page and on the *
 * Product page. Composed of:                                          *
 *   · a "browser chrome" bar with the SRES workspace label            *
 *   · three count-up KPI tiles                                        *
 *   · a live isometric 3D kriged block model (SVG)                    *
 *   · a variogram fit and classification stack                        *
 *   · an OK-runtime status strip                                      *
 *                                                                     *
 * The cockpit is content-agnostic: pass your own `label`, `kpis`,     *
 * `variogram`, `classification` and `status` to reuse the composition *
 * on any hero. Sensible defaults mirror the flagship Landing example.*
 * ================================================================== */
export const DEFAULT_KPIS = [
  { label: "ORE TONNES", to: 14.2, decimals: 1, suffix: " Mt",  delta: "▲ 4.3%",   deltaColor: "text-brand-blue" },
  { label: "AVG GRADE",  to: 1.42, decimals: 2, suffix: " g/t", delta: "Au",       deltaColor: "text-slate-500"  },
  { label: "NPV @ 8%",   to: 428,  prefix: "$", suffix: " M",   delta: "▲ OK est.", deltaColor: "text-brand-blue" },
];

export const DEFAULT_CLASSIFICATION = [
  { label: "Measured", pct: 42 },
  { label: "Indicated", pct: 35 },
  { label: "Inferred", pct: 23 },
];

export const DEFAULT_VARIOGRAM = { r2: "0.94", range: "180 m", sill: "0.72", nugget: "0.08" };

export const DEFAULT_STATUS = {
  left: "OK · 3.1 s per 100k blocks",
  right: "Snapshot · 2m ago · Reproducible",
};

export function KpiTile({ label, to, prefix = "", suffix = "", decimals = 0, delta, deltaColor = "text-brand-blue" }) {
  return (
    <div className="rounded-xl border border-slate-100 p-3.5 bg-white">
      <div className="text-[9.5px] uppercase tracking-[0.22em] text-slate-400 font-mono">{label}</div>
      <div className="mt-1.5 font-heading font-black text-slate-900 text-[22px] tracking-tight leading-tight">
        <CountUp to={to} prefix={prefix} suffix={suffix} decimals={decimals} duration={1.4} />
      </div>
      <div className={`mt-0.5 text-[11px] font-mono ${deltaColor}`}>{delta}</div>
    </div>
  );
}

export default function ResourceCockpit({
  label = "SRES · Resource Cockpit",
  kpis = DEFAULT_KPIS,
  variogram = DEFAULT_VARIOGRAM,
  classification = DEFAULT_CLASSIFICATION,
  status = DEFAULT_STATUS,
  testid = "hero-dashboard-mockup",
}) {
  return (
    <div className="relative" data-testid={testid}>
      {/* Subtle halo */}
      <div className="absolute -inset-6 bg-slate-900/[0.04] rounded-[28px] blur-2xl" aria-hidden />

      <div className="relative bg-white border border-slate-200 rounded-2xl shadow-[0_30px_80px_-40px_rgba(15,23,42,0.25)] overflow-hidden">
        {/* Chrome bar */}
        <div className="h-10 border-b border-slate-100 flex items-center px-4 gap-2 bg-slate-50/60">
          <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
          <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
          <div className="flex-1" />
          <div className="text-[10.5px] tracking-[0.30em] text-slate-400 font-mono uppercase">{label}</div>
          <div className="flex-1" />
        </div>

        {/* KPI row */}
        <div className="p-5 grid grid-cols-3 gap-3 border-b border-slate-100">
          {kpis.map((k) => <KpiTile key={k.label} {...k} />)}
        </div>

        {/* Main visualisation area */}
        <div className="p-5 grid grid-cols-[1.35fr,1fr] gap-4">
          {/* Isometric block model */}
          <div className="rounded-xl border border-slate-100 p-4 bg-gradient-to-br from-slate-50/60 to-white relative overflow-hidden aspect-[5/4]">
            <div className="absolute left-3 top-3 flex items-center gap-1 text-[11px] text-slate-400 font-mono z-10">
              <Compass size={14} weight="bold" className="text-brand-blue" />
              N
            </div>
            <div className="absolute right-3 top-3 text-[10px] text-slate-400 font-mono z-10">3D · Kriged</div>
            <IsometricBlockModel />
            <div className="absolute right-3 bottom-3 text-[10px] text-slate-400 font-mono">500 m</div>
            <div className="absolute left-3 bottom-3 flex items-center gap-2 text-[10px] text-slate-500 font-mono">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#1E6FFF]" />LG</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#22C7B0]" />MG</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-[#F59E0B]" />HG</span>
            </div>
          </div>

          {/* Variogram + Classification stack */}
          <div className="space-y-3">
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">Variogram · fit</div>
                <div className="text-[10px] font-mono text-brand-blue">R² {variogram.r2}</div>
              </div>
              <VariogramViz />
              <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-slate-400">
                <span>Range · {variogram.range}</span>
                <span>Sill · {variogram.sill}</span>
                <span>Nugget · {variogram.nugget}</span>
              </div>
            </div>
            <div className="rounded-xl border border-slate-100 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-400 font-mono">Classification</div>
              <ClassificationBars rows={classification} />
            </div>
          </div>
        </div>

        {/* Bottom status strip */}
        <div className="border-t border-slate-100 px-5 h-9 flex items-center justify-between text-[10.5px] font-mono text-slate-500">
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {status.left}
          </div>
          {status.right ? <div className="hidden sm:block">{status.right}</div> : null}
        </div>
      </div>
    </div>
  );
}
